import { CustomerAddress } from 'types/Customer';

export interface SelectedPlace {
    formattedAddress: string;
    streetNumber: string;
    route: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
    latitude: number;
    longitude: number;
    subpremise: string;
}

const WALKING_ADDRESS_FIELD_LABELS: Record<string, string> = {
    address_line_one: 'Home Address',
    city: 'City',
    state: 'State',
    zipcode: 'Zip',
    zip_code: 'Zip',
    zip: 'Zip'
};

const WALKING_ADDRESS_ERROR_FALLBACK = 'Oops, something went wrong.';

export const WALKING_ADDRESS_DETAILS_ERROR =
    "We couldn't get the details for this address. Please try again.";

const streetFromPlace = (place: SelectedPlace): string =>
    [place.streetNumber, place.route].filter(part => part.trim().length > 0).join(' ') ||
    place.formattedAddress;

export const isSelectedPlaceComplete = (place?: SelectedPlace): place is SelectedPlace =>
    Boolean(place?.city.trim() && place.zipcode.trim());

export const walkingAddressFieldErrorMessage = (data: unknown): string => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return WALKING_ADDRESS_ERROR_FALLBACK;
    }

    for (const [key, value] of Object.entries(data)) {
        const field = /^addresses\.(.+)$/.exec(key)?.[1];
        const label = field ? WALKING_ADDRESS_FIELD_LABELS[field] : undefined;
        const message = Array.isArray(value) ? value[0] : undefined;

        if (label && typeof message === 'string' && message.trim().length > 0) {
            return `${label} ${message}`;
        }
    }

    return WALKING_ADDRESS_ERROR_FALLBACK;
};

export const buildCustomerAddressFromPlace = (place: SelectedPlace): CustomerAddress => {
    return {
        addressLineOne: streetFromPlace(place),
        addressLineTwo: place.subpremise.trim() || undefined,
        city: place.city,
        state: place.state,
        zipCode: place.zipcode,
        country: place.country,
        active: true
    };
};

export const buildCustomerAddressFromFields = (fields: {
    addressLineOne: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
}): CustomerAddress => ({
    addressLineOne: fields.addressLineOne,
    city: fields.city,
    state: fields.state,
    zipCode: fields.zipCode,
    country: fields.country,
    active: true
});

export const upsertMainCustomerAddress = (
    addresses: Array<CustomerAddress> | undefined,
    next: CustomerAddress
): CustomerAddress => {
    const list = addresses ?? [];
    const main =
        list.find(address => address.main && address.active) ??
        list.find(address => address.active);

    if (main?.id != null) {
        return { ...next, id: main.id };
    }

    return next;
};
